/**
 * REST API client using TanStack Query.
 */

import { QueryClient } from '@tanstack/react-query';
import { API_CONFIG, QUERY_CONFIG } from '../config';
import { useAuthStore } from '../stores/authStore';
import type {
  LinesResponse,
  LineDetailResponse,
  StopsSearchResponse,
  StopDetailResponse,
  JourneysResponse,
  JourneyDetailResponse,
  JourneyRouteResponse,
  JourneyScheduleResponse,
  ViewportResponse,
  DualSourceViewportResponse,
  HealthResponse,
  PollerBboxFeatureCollection,
  DynamicConfigResponse,
  DynamicConfigUpdateResponse,
  DynamicConfig,
} from '../types/api';

// =============================================================================
// QUERY CLIENT
// =============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_CONFIG.STALE_TIME_MS,
      gcTime: QUERY_CONFIG.GC_TIME_MS,
      retry: QUERY_CONFIG.RETRY_COUNT,
      retryDelay: (attemptIndex) =>
        Math.min(QUERY_CONFIG.RETRY_DELAY_MS * 2 ** attemptIndex, 30000),
    },
  },
});

// =============================================================================
// API HELPERS
// =============================================================================

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  return response.json();
}

/**
 * Fetch JSON with authentication header.
 * Use for admin-only endpoints like /config/dynamic.
 */
async function authenticatedFetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const { accessToken } = useAuthStore.getState();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  return response.json();
}

// =============================================================================
// LINES API
// =============================================================================

export interface GetLinesParams {
  transport_mode?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
}

export async function getLines(params: GetLinesParams = {}): Promise<LinesResponse> {
  const searchParams = new URLSearchParams();
  if (params.transport_mode) searchParams.set('transport_mode', params.transport_mode);
  if (params.active !== undefined) searchParams.set('active', String(params.active));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));

  const query = searchParams.toString();
  const url = `${API_CONFIG.REST_BASE}/lines${query ? `?${query}` : ''}`;
  return fetchJson<LinesResponse>(url);
}

export async function getLine(lineId: string): Promise<LineDetailResponse> {
  return fetchJson<LineDetailResponse>(`${API_CONFIG.REST_BASE}/lines/${lineId}`);
}

// =============================================================================
// STOPS API
// =============================================================================

export interface GetStopsParams {
  q?: string;
  lat?: number;
  lon?: number;
  radius?: number;
  limit?: number;
}

export async function getStops(params: GetStopsParams = {}): Promise<StopsSearchResponse> {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.set('q', params.q);
  if (params.lat !== undefined) searchParams.set('lat', String(params.lat));
  if (params.lon !== undefined) searchParams.set('lon', String(params.lon));
  if (params.radius) searchParams.set('radius', String(params.radius));
  if (params.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  const url = `${API_CONFIG.REST_BASE}/stops${query ? `?${query}` : ''}`;
  return fetchJson<StopsSearchResponse>(url);
}

export async function getStop(stopId: string): Promise<StopDetailResponse> {
  return fetchJson<StopDetailResponse>(`${API_CONFIG.REST_BASE}/stops/${stopId}`);
}

// =============================================================================
// JOURNEYS API
// =============================================================================

export interface GetJourneysParams {
  line_id?: string;
  status?: 'active' | 'inactive' | 'all';
  limit?: number;
  offset?: number;
}

export async function getJourneys(params: GetJourneysParams = {}): Promise<JourneysResponse> {
  const searchParams = new URLSearchParams();
  if (params.line_id) searchParams.set('line_id', params.line_id);
  if (params.status) searchParams.set('status', params.status);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));

  const query = searchParams.toString();
  const url = `${API_CONFIG.REST_BASE}/journeys${query ? `?${query}` : ''}`;
  return fetchJson<JourneysResponse>(url);
}

export async function getJourney(identifier: string): Promise<JourneyDetailResponse> {
  return fetchJson<JourneyDetailResponse>(`${API_CONFIG.REST_BASE}/journeys/${identifier}`);
}

export async function getJourneyRoute(identifier: string): Promise<JourneyRouteResponse> {
  return fetchJson<JourneyRouteResponse>(`${API_CONFIG.REST_BASE}/journeys/${identifier}/route`);
}

export async function getJourneySchedule(identifier: string): Promise<JourneyScheduleResponse> {
  return fetchJson<JourneyScheduleResponse>(`${API_CONFIG.REST_BASE}/journeys/${identifier}/schedule`);
}

// =============================================================================
// POSITIONS API (for fallback polling)
// =============================================================================

export interface GetViewportParams {
  bbox: string; // 'minLon,minLat,maxLon,maxLat'
  limit?: number;
  sources?: 'all' | 'v1-da-gps' | 'v4-estimated';
}

export async function getViewport(
  params: GetViewportParams
): Promise<ViewportResponse | DualSourceViewportResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('bbox', params.bbox);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.sources) searchParams.set('sources', params.sources);

  const url = `${API_CONFIG.REST_BASE}/viewport?${searchParams.toString()}`;
  return fetchJson<ViewportResponse | DualSourceViewportResponse>(url);
}

// =============================================================================
// HEALTH API
// =============================================================================

export async function getHealth(): Promise<HealthResponse> {
  return fetchJson<HealthResponse>(`${API_CONFIG.REST_BASE}/health`);
}

// =============================================================================
// CONFIG API
// =============================================================================

export async function getPollerBboxes(): Promise<PollerBboxFeatureCollection> {
  return fetchJson<PollerBboxFeatureCollection>(`${API_CONFIG.REST_BASE}/pollers/geojson`);
}

// =============================================================================
// DYNAMIC CONFIG API
// =============================================================================

/**
 * Get current dynamic configuration.
 * Requires admin authentication.
 */
export async function getDynamicConfig(): Promise<DynamicConfigResponse> {
  return authenticatedFetchJson<DynamicConfigResponse>(`${API_CONFIG.REST_BASE}/config/dynamic`);
}

/**
 * Update dynamic configuration with partial updates.
 * Only include the sections/fields you want to change.
 * Requires admin authentication.
 */
export async function updateDynamicConfig(
  updates: Partial<{
    rate_limits: Partial<DynamicConfig['rate_limits']>;
    v1da: Partial<DynamicConfig['v1da']>;
  }>
): Promise<DynamicConfigUpdateResponse> {
  return authenticatedFetchJson<DynamicConfigUpdateResponse>(`${API_CONFIG.REST_BASE}/config/dynamic`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// =============================================================================
// DEBUG CURL API (admin-only)
// =============================================================================

export interface DebugCurlResponse {
  journey_gid: string;
  type: string;
  curl_command: string;
  db_position: {
    latitude: number;
    longitude: number;
    data_source: string;
    time: string;
    age_seconds: number;
    v4_position?: {
      latitude: number;
      longitude: number;
      time: string;
      age_seconds: number;
    };
  } | null;
  token_info: {
    type: string;
    expires_in_seconds?: number;
    valid_for_seconds?: number;
  };
  notes: string[];
  generated_at: string;
}

/**
 * Generate a debug curl command for querying upstream Västtrafik APIs.
 * Admin-only. Returns a ready-to-paste curl with live auth tokens.
 */
export async function getDebugCurl(
  journeyGid: string,
  type: 'v4' | 'v1da',
  lat?: number,
  lon?: number
): Promise<DebugCurlResponse> {
  const params = new URLSearchParams({ type });
  if (lat !== undefined) params.set('lat', String(lat));
  if (lon !== undefined) params.set('lon', String(lon));

  return authenticatedFetchJson<DebugCurlResponse>(
    `${API_CONFIG.REST_BASE}/debug/curl/${encodeURIComponent(journeyGid)}?${params}`
  );
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const queryKeys = {
  lines: {
    all: ['lines'] as const,
    list: (params: GetLinesParams) => ['lines', 'list', params] as const,
    detail: (id: string) => ['lines', 'detail', id] as const,
  },
  stops: {
    all: ['stops'] as const,
    search: (params: GetStopsParams) => ['stops', 'search', params] as const,
    detail: (id: string) => ['stops', 'detail', id] as const,
  },
  journeys: {
    all: ['journeys'] as const,
    list: (params: GetJourneysParams) => ['journeys', 'list', params] as const,
    detail: (id: string) => ['journeys', 'detail', id] as const,
    route: (id: string) => ['journeys', 'route', id] as const,
    schedule: (id: string) => ['journeys', 'schedule', id] as const,
  },
  aois: {
    all: ['aois'] as const,
    list: (activeOnly: boolean) => ['aois', 'list', activeOnly] as const,
  },
  pollerBboxes: {
    all: ['pollerBboxes'] as const,
  },
  health: ['health'] as const,
  config: ['config'] as const,
  dynamicConfig: ['dynamicConfig'] as const,
};
